import React, { useEffect, useState } from "react";
import Translation from "utils/Translation";
import { getHotelLogoSrc } from "utils/hotelLogos";
// @ts-ignore - ignore type resolution for png assets
import imgEau from "../img/eau.png";
// @ts-ignore - ignore type resolution for png assets
import imgCo2 from "../img/co2.png";
// @ts-ignore - ignore type resolution for png assets
import imgPersonne from "../img/personne.png";

const formatBreakEven = (months) => {
  if (!months || isNaN(months)) return "N/A";

  const totalMonths = Math.round(parseFloat(months));
  const years = Math.floor(totalMonths / 12);
  const remainingMonths = totalMonths % 12;

  if (years > 0 && remainingMonths > 0) {
    return `${years} an${years > 1 ? "s" : ""} et ${remainingMonths} mois`;
  } else if (years > 0) {
    return `${years} an${years > 1 ? "s" : ""}`;
  } else {
    return `${totalMonths} mois`;
  }
};

const formatNumberWithSpaces = (number) => {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

const roundToTen = (number) => {
  return Math.round(number / 10) * 10;
};

const roundToUnit = (number) => {
  return Math.round(number);
};

const formatCO2 = (number) => {
  return Number(number).toFixed(2);
};

const formatHotelBrandName = (name) => {
  if (!name || typeof name !== "string") return "";
  return name
    .split(/[_\s]+/)
    .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : ""))
    .join(" ");
};

const getBilanByPeriod = (results, period) => {
  const multiplier = {
    "1 mois": 1,
    "1 an": 12,
    "5 ans": 60,
  }[period];

  return {
    waterSaved: formatNumberWithSpaces(roundToTen(Math.round(results.waterSaved * multiplier))),
    energySaved: formatNumberWithSpaces(roundToTen(Math.round(results.energySaved * multiplier))),
    co2Saved: formatCO2(results.co2Saved * multiplier),
    peopleHelped: formatNumberWithSpaces(roundToUnit(results.peopleHelped * multiplier)),
  };
};

/** Trois options avec la valeur du milieu = valeur rentrée (formulaire). */
const getPresetsWithMiddle = (userValue, step, minVal, maxVal, decimals = 2) => {
  const round = (n) => (decimals >= 0 ? Math.round(n * 10 ** decimals) / 10 ** decimals : n);
  const uv = Number(userValue);
  const low = round(Math.max(minVal, Math.min(uv - step, uv)));
  const high = round(Math.min(maxVal, Math.max(uv + step, uv)));
  const mid = round(uv);
  const all = [low, mid, high].sort((a, b) => a - b);
  const unique = [];
  for (let i = 0; i < all.length; i += 1) {
    if (!unique.includes(all[i])) {
      unique.push(all[i]);
    }
  }
  if (unique.length >= 3) return unique;
  if (unique.length === 2) return [unique[0], mid, unique[1]];
  return [mid, mid, mid];
};

/**
 * @param {{ results: any; onEdit: () => void; onDevisClick: () => void; onParamsChange?: (updatedParams: { occupationRate?: number; energyPrice?: number; waterPrice?: number; }) => void }} props
 */
const SimulatorResults = ({ results, onEdit, onDevisClick, onParamsChange }) => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [params, setParams] = useState({
    occupationRate: results.occupationRate,
    energyPrice: results.energyPrice,
    waterPrice: results.waterPrice,
  });

  useEffect(() => {
    setParams({
      occupationRate: results.occupationRate,
      energyPrice: results.energyPrice,
      waterPrice: results.waterPrice,
    });
  }, [results]);

  // Presets centrés sur les valeurs rentrées (formulaire) : valeur du milieu = nos paramètres
  const energyPresets = getPresetsWithMiddle(Number(results.energyPrice), 0.05, 0.05, 0.5);
  const waterPresets = getPresetsWithMiddle(Number(results.waterPrice), 1, 0.3, 10);
  const occupationPresets = getPresetsWithMiddle(Number(results.occupationRate), 15, 10, 100, 0);

  const bilan = getBilanByPeriod(results, "1 an");
  // Gains RSE : à l'année uniquement, avec coefficient 0,55
  const RSE_COEFF = 0.55;
  const bilanRSE = {
    waterSaved: formatNumberWithSpaces(roundToTen(Math.round(results.waterSaved * 12 * RSE_COEFF))),
    co2Saved: formatCO2(results.co2Saved * 12 * RSE_COEFF),
    peopleHelped: formatNumberWithSpaces(roundToUnit(results.peopleHelped * 12 * RSE_COEFF)),
  };
  // Coefficients ROI : conservatrice x0,55, positive x0,85 (appliqués aux gains bruts ; coût inchangé)
  const ROI_CONSERVATRICE = 0.55;
  const ROI_POSITIVE = 0.85;
  const gainBrutConservatrice = results.moneySaved * ROI_CONSERVATRICE;
  const gainBrutPositive = results.moneySaved * ROI_POSITIVE;
  const gainNetConservatrice = gainBrutConservatrice - results.rentalCostPerMonth;
  const gainNetPositive = gainBrutPositive - results.rentalCostPerMonth;
  const gain3ansConservatrice = gainNetConservatrice * 36;
  const gain3ansPositive = gainNetPositive * 36;

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleDevisClick = async () => {
    // Réinitialiser l'erreur
    setEmailError("");

    // Validation de l'email
    if (!email) {
      setEmailError(Translation({ tag: "Simulator_Results_EmailRequired" }));
      return;
    }

    if (!validateEmail(email)) {
      setEmailError(Translation({ tag: "Simulator_Results_EmailInvalid" }));
      return;
    }

    setIsLoading(true);

    // Préparation des données (uniquement les inputs utilisateur)
    const dataToSend = {
      email,
      hotelBrand: results.selectedHotel,
      hotelType: results.hotelType,
      nbRooms: results.nbRooms,
      occupationRate: results.occupationRate,
      energyPrice: results.energyPrice,
      waterPrice: results.waterPrice,
    };

    try {
      const response = await fetch("/api/devis", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(dataToSend),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Erreur serveur:", errorData);
        throw new Error(errorData.message || "Erreur lors de l'envoi du devis");
      }

      // Si tout s'est bien passé, on appelle la fonction de navigation
      onDevisClick();
    } catch (error) {
      console.error("Error sending devis:", error);
      setEmailError(Translation({ tag: "Simulator_Results_EmailSendError" }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="simulator_P2_header">
        <button type="button" className="simulator_P2_back" onClick={onEdit}>
          <span className="simulator_P2_back-icon">←</span>
          <span className="return-btn">
            <Translation tag="Simulator_Results_ModifyInfo" />
          </span>
        </button>
      </div>

      <h2 className="simulator_P2_main-title">
        <Translation tag="Simulator_Results_Title" />
      </h2>

      <div className="simulator_P2_infos">
        <div className="simulator_P2_infos-content">
          <div className="simulator_P2_infos-text">
            <h5 className="simulator_P2_section-title">
              <Translation tag="Simulator_Results_Establishment" />
            </h5>
            <div className="simulator_P2_infos-row">
              <p>
                <Translation tag="Simulator_Results_NumRooms" /> :{" "}
                <span className="simulator_P2_highlight">
                  {results.nbRooms} <Translation tag="Room_label_rooms" />
                </span>
              </p>
              <p>
                <Translation tag="Simulator_Results_HotelBrand" /> :{" "}
                <span className="simulator_P2_highlight">{formatHotelBrandName(results.selectedHotel)}</span>
              </p>
              <p>
                <Translation tag="Simulator_Results_Reference" /> : <span className="simulator_P2_highlight">{results.hotelType}</span>
              </p>
            </div>
          </div>
          {getHotelLogoSrc(results.selectedHotel) && (
            <div className="simulator_P2_infos-logo">
              <img src={getHotelLogoSrc(results.selectedHotel)} alt="" aria-hidden="true" />
            </div>
          )}
        </div>
      </div>

      {/* Bloc des 3 cartes : Paramètres, ROI, Gains RSE */}
      <div className="simulator_P2_cards-row">
        {/* Card 1 : Vos paramètres (jauges indicatives, non interactives) */}
        <div className="simulator_P2_card simulator_P2_column simulator_P2_panel simulator_P2_params-column">
          <h5 className="simulator_P2_block-title">
            <Translation tag="Simulator_Results_Params_Title" />
          </h5>
          <p className="simulator_P2_block-subtitle">
            <Translation tag="Simulator_Results_Params_Subtitle" />
          </p>

          <div className="simulator_P2_params-grid">
            {/* Prix de l'énergie */}
            <div className="simulator_P2_param-field">
              <label className="simulator_P2_param-label">
                <Translation tag="Simulator_Results_EnergyPrice_Label" />
              </label>
              <div className="simulator_P2_gauge-row">
                <div className="simulator_P2_gauge-scale">
                  <span>
                    <Translation tag="Simulator_Results_EnergyPrice_Min" />
                  </span>
                  <span>
                    <Translation tag="Simulator_Results_EnergyPrice_Max" />
                  </span>
                </div>
                <div className="simulator_P2_gauge-bar simulator_P2_gauge-bar--price">
                  {(() => {
                    const pos = Math.min(
                      100,
                      Math.max(
                        0,
                        ((results.energyPrice - 0.06) / (0.3 - 0.06)) * 100,
                      ),
                    );
                    return (
                      <>
                        <div
                          className="simulator_P2_gauge-indicator"
                          style={{ left: `${pos}%` }}
                        />
                        <div
                          className="simulator_P2_gauge-label"
                          style={{ left: `${pos}%` }}
                        >
                          {results.energyPrice.toFixed(2).replace(".", ",")}{" "}
                          <Translation tag="Simulator_Results_EnergyPrice_Unit" />
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Prix de l'eau */}
            <div className="simulator_P2_param-field">
              <label className="simulator_P2_param-label">
                <Translation tag="Simulator_Results_WaterPrice_Label" />
              </label>
              <div className="simulator_P2_gauge-row">
                <div className="simulator_P2_gauge-scale">
                  <span>
                    <Translation tag="Simulator_Results_WaterPrice_Min" />
                  </span>
                  <span>
                    <Translation tag="Simulator_Results_WaterPrice_Max" />
                  </span>
                </div>
                <div className="simulator_P2_gauge-bar simulator_P2_gauge-bar--price">
                  {(() => {
                    const pos = Math.min(
                      100,
                      Math.max(
                        0,
                        ((results.waterPrice - 0.5) / (7 - 0.5)) * 100,
                      ),
                    );
                    return (
                      <>
                        <div
                          className="simulator_P2_gauge-indicator"
                          style={{ left: `${pos}%` }}
                        />
                        <div
                          className="simulator_P2_gauge-label"
                          style={{ left: `${pos}%` }}
                        >
                          {results.waterPrice.toFixed(2).replace(".", ",")}{" "}
                          <Translation tag="Simulator_Results_WaterPrice_Unit" />
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Taux d'occupation */}
            <div className="simulator_P2_param-field simulator_P2_param-field--occupation">
              <label className="simulator_P2_param-label">
                <Translation tag="Simulator_Results_OccupationRate_Label" />
              </label>
              <div className="simulator_P2_gauge-row">
                <div className="simulator_P2_gauge-scale">
                  <span>
                    <Translation tag="Simulator_Results_OccupationRate_Min" />
                  </span>
                  <span>
                    <Translation tag="Simulator_Results_OccupationRate_Max" />
                  </span>
                </div>
                <div className="simulator_P2_gauge-bar">
                  {(() => {
                    const pos = Math.min(
                      100,
                      Math.max(
                        0,
                        ((results.occupationRate - 30) / (100 - 30)) * 100,
                      ),
                    );
                    return (
                      <>
                        <div
                          className="simulator_P2_gauge-indicator"
                          style={{ left: `${pos}%` }}
                        />
                        <div
                          className="simulator_P2_gauge-label"
                          style={{ left: `${pos}%` }}
                        >
                          {results.occupationRate}%
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Card 2 : Votre estimation de ROI (2 colonnes : conservatrice / positive) */}
        <div className="simulator_P2_card simulator_P2_column simulator_P2_panel">
          <h5 className="simulator_P2_block-title">
            <Translation tag="Simulator_Results_ROI_Title" />
          </h5>
          <p className="simulator_P2_roi-note simulator_P2_roi-note--top">
            <Translation tag="Simulator_Results_ROI_Note" />
          </p>

          <div className="simulator_P2_roi-twocol">
            <div className="simulator_P2_roi-thead">
              <div className="simulator_P2_roi-th-label" />
              <div
                className="simulator_P2_roi-th-col simulator_P2_roi-th-col--conservatrice"
                style={{ borderTopRightRadius: "10px", borderBottomRightRadius: "10px" }}
              >
                <Translation tag="Simulator_Results_ROI_Conservative_Title" />
              </div>
              <div className="simulator_P2_roi-th-col simulator_P2_roi-th-col--positive">
                <Translation tag="Simulator_Results_ROI_Positive_Title" />
              </div>
            </div>
            <div className="simulator_P2_roi-tbody">
              <div className="simulator_P2_roi-tr">
                <div className="simulator_P2_roi-td-label">
                  <div className="simulator_P2_roi-label-wrap">
                      <span className="simulator_P2_roi-label">
                        <Translation tag="Simulator_Results_ROI_GrossGain_Label" />
                      </span>
                    <span className="simulator_P2_roi-tooltip" role="tooltip">
                        <Translation tag="Simulator_Results_ROI_GrossGain_Tooltip" />
                    </span>
                  </div>
                </div>
                <div className="simulator_P2_roi-td-value simulator_P2_roi-td-value--conservatrice">
                  <span className="simulator_P2_roi-value simulator_P2_roi-value--primary">+ {formatNumberWithSpaces(roundToTen(gainBrutConservatrice))} €</span>
                </div>
                <div className="simulator_P2_roi-td-value simulator_P2_roi-td-value--positive">
                  <span className="simulator_P2_roi-value simulator_P2_roi-value--primary">+ {formatNumberWithSpaces(roundToTen(gainBrutPositive))} €</span>
                </div>
              </div>
              <div className="simulator_P2_roi-tr">
                <div className="simulator_P2_roi-td-label">
                  <div className="simulator_P2_roi-label-wrap">
                      <span className="simulator_P2_roi-label">
                        <Translation tag="Simulator_Results_ROI_Cost_Label" />
                      </span>
                    <span className="simulator_P2_roi-tooltip" role="tooltip">
                        <Translation tag="Simulator_Results_ROI_Cost_Tooltip" />
                    </span>
                  </div>
                </div>
                <div className="simulator_P2_roi-td-value simulator_P2_roi-td-value--conservatrice">
                  <span className="simulator_P2_roi-value">{formatNumberWithSpaces(roundToTen(results.rentalCostPerMonth))} €</span>
                </div>
                <div className="simulator_P2_roi-td-value simulator_P2_roi-td-value--positive">
                  <span className="simulator_P2_roi-value">{formatNumberWithSpaces(roundToTen(results.rentalCostPerMonth))} €</span>
                </div>
              </div>
              <div className="simulator_P2_roi-tr">
                <div className="simulator_P2_roi-td-label">
                  <div className="simulator_P2_roi-label-wrap">
                      <span className="simulator_P2_roi-label">
                        <Translation tag="Simulator_Results_ROI_NetGain_Label" />
                      </span>
                    <span className="simulator_P2_roi-tooltip" role="tooltip">
                        <Translation tag="Simulator_Results_ROI_NetGain_Tooltip" />
                    </span>
                  </div>
                </div>
                <div className="simulator_P2_roi-td-value simulator_P2_roi-td-value--conservatrice">
                  <span className={`simulator_P2_roi-value ${gainNetConservatrice >= 0 ? "simulator_P2_roi-value--primary" : "simulator_P2_roi-value--negative"}`}>{gainNetConservatrice >= 0 ? `+ ${formatNumberWithSpaces(roundToTen(gainNetConservatrice))} €` : `- ${formatNumberWithSpaces(roundToTen(Math.abs(gainNetConservatrice)))} €`}</span>
                </div>
                <div className="simulator_P2_roi-td-value simulator_P2_roi-td-value--positive">
                  <span className={`simulator_P2_roi-value ${gainNetPositive >= 0 ? "simulator_P2_roi-value--primary" : "simulator_P2_roi-value--negative"}`}>{gainNetPositive >= 0 ? `+ ${formatNumberWithSpaces(roundToTen(gainNetPositive))} €` : `- ${formatNumberWithSpaces(roundToTen(Math.abs(gainNetPositive)))} €`}</span>
                </div>
              </div>
              <div className="simulator_P2_roi-tr">
                <div className="simulator_P2_roi-td-label">
                  <div className="simulator_P2_roi-label-wrap simulator_P2_roi-label-wrap--last">
                      <span className="simulator_P2_roi-label">
                        <Translation tag="Simulator_Results_ROI_3YearsGain_Label" />
                      </span>
                    <span className="simulator_P2_roi-tooltip" role="tooltip">
                        <Translation tag="Simulator_Results_ROI_3YearsGain_Tooltip" />
                    </span>
                  </div>
                </div>
                <div className="simulator_P2_roi-td-value simulator_P2_roi-td-value--conservatrice">
                  <span className="simulator_P2_roi-value simulator_P2_roi-value--accent">{formatNumberWithSpaces(roundToTen(gain3ansConservatrice))} €</span>
                </div>
                <div className="simulator_P2_roi-td-value simulator_P2_roi-td-value--positive">
                  <span className="simulator_P2_roi-value simulator_P2_roi-value--accent">{formatNumberWithSpaces(roundToTen(gain3ansPositive))} €</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Card 3 : Vos Gains RSE (toujours 1 an, énergie calculée mais non affichée) */}
        <div className="simulator_P2_card simulator_P2_column simulator_P2_panel simulator_P2_bilan-block">
          <h5 className="simulator_P2_block-title">
            <Translation tag="Simulator_Results_RSE_Title" />
          </h5>
          <p className="simulator_P2_rse-description">
            <Translation tag="Simulator_Results_RSE_Description" />
          </p>
          <div className="simulator_P2_rse-grid">
            <div className="simulator_P2_rse-block">
              <div className="simulator_P2_rse-main">
                <img src={imgEau} alt="" className="simulator_P2_bilan-icon-img" />
                <span className="simulator_P2_rse-value">
                  {bilanRSE.waterSaved} <Translation tag="Simulator_Results_RSE_Water_Unit" />
                </span>
              </div>
              <div className="simulator_P2_rse-tag">
                <span>
                  <Translation tag="Simulator_Results_RSE_Water_Label" />
                </span>
              </div>
            </div>

            <div className="simulator_P2_rse-block">
              <div className="simulator_P2_rse-main">
                <img src={imgCo2} alt="" className="simulator_P2_bilan-icon-img" />
                <span className="simulator_P2_rse-value">
                  {bilanRSE.co2Saved} <Translation tag="Simulator_Results_RSE_CO2_Unit" />
                </span>
              </div>
              <div className="simulator_P2_rse-tag">
                <span>
                  <Translation tag="Simulator_Results_RSE_CO2_Label" />
                </span>
              </div>
            </div>

            <div className="simulator_P2_rse-block">
              <div className="simulator_P2_rse-main">
                <img src={imgPersonne} alt="" className="simulator_P2_bilan-icon-img" />
                <span className="simulator_P2_rse-value">{bilanRSE.peopleHelped}</span>
              </div>
              <div className="simulator_P2_rse-tag simulator_P2_rse-tag--people">
                <span>
                  <Translation tag="Simulator_Results_RSE_People_Label" />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Card 4 : Être recontacté (maquette Etre_recontacte) */}
      <div className="simulator_P2_card simulator_P2_card--devis devis-container">
        <h2 className="devis-title">
          <Translation tag="Simulator_Results_Devis_Title" />
        </h2>
        <p className="devis-description">
          <Translation tag="Simulator_Results_Devis_Description" />
        </p>
        <div className="simulator_P2_devis">
          <div className="email-input-container">
            <input
              className={`simulator_P2_devis-email ${emailError ? "error" : ""}`}
              type="email"
              placeholder={Translation({ tag: "Simulator_Results_EmailPlaceholder" })}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError("");
              }}
            />
            {emailError && <p className="email-error">{emailError}</p>}
          </div>
          <button className="simulator_P2_devis-button" onClick={handleDevisClick} disabled={isLoading}>
            {isLoading ? <Translation tag="Simulator_Results_SendingInProgress" /> : <Translation tag="Simulator_Results_SendQuote" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SimulatorResults;
